// Shared booking-label resolver (design-notes-booking-types-reasons-titles, Feature D).
// Used by the DayView chips and the BookingDetail header so the human-readable name is
// resolved one way everywhere. Priority: title → person (client/student) [+ " – activity"
// when allowed] → activity name → booking-kind name → "Booking".
//
// "Client name" here = the booking's first student/client participant (Booking.trainee).
// In this domain a client rides as a participant (capacity 'client' → role 'student'), so the
// trainee field IS the client/student. No client_id lookup or backend call is needed.

import type { Booking, BookingKind, TrainingActivity, BookingTrainee } from '../opsBoardTypes';

// Person portion for internal bookings: preferred_name → full_name (pilot_code not carried on
// the trainee shape). Returns null when there is no person on the booking.
export function personName(t: BookingTrainee | null): string | null {
  if (!t) return null;
  return (t.preferred_name?.trim() || t.full_name?.trim()) || null;
}

function activityName(b: Booking, activities: Map<string, TrainingActivity>): string | null {
  return b.activity_id ? activities.get(b.activity_id)?.name ?? null : null;
}

// The chip / header label. `allowSuffix` appends " – activity" to a person name when the chip
// is wide enough (the caller gates on width).
export function bookingLabel(
  b: Booking,
  kinds: Map<string, BookingKind>,
  activities: Map<string, TrainingActivity>,
  opts: { allowSuffix?: boolean } = {},
): string {
  const title = b.title?.trim();
  if (title) return title;

  const person = personName(b.trainee);
  const act = activityName(b, activities);
  // ⚠ THE EN DASH STAYS. Ben, 2026-09-06: the no-dash rule is about general PROSE. As a
  // SEPARATOR between the elements of a label it is fine, and "Fred Smith – Climbing &
  // Descending" is exactly the intended shape. Do not "fix" it, and do not re-raise it.
  //
  // The one worry was a DANGLING separator — "Fred Smith – " with nothing after it. That is
  // structurally impossible, and by construction rather than by luck: the dash exists only
  // inside the branch of this ternary that REQUIRES `act`, so with no activity the function
  // returns the person alone and the dash is never emitted.
  if (person) return opts.allowSuffix && act ? `${person} – ${act}` : person;
  if (act) return act;

  const kind = b.booking_kind_id ? kinds.get(b.booking_kind_id) : undefined;
  return kind?.name ?? 'Booking';
}

/**
 * The TASK row's label (PR-000058 follow-up, Ben 2026-09-06; recomposed 0.12.5, Ben 2026-09-07).
 *
 *     title  →  'Kind - Activity'  →  'Kind'  →  '' (blank)
 *
 * ⚠ THE KIND IS THE ANCHOR, NOT THE ACTIVITY. Ben's rule, 2026-09-07, as a table:
 *
 *     title present            → the title
 *     no title, kind+activity  → "Kind - Activity"
 *     no title, kind only      → "Kind"
 *     no title, no kind        → blank
 *
 * The previous order returned the ACTIVITY ALONE when there was no title, and only fell to the
 * kind when there was no activity either. So the visible change is twofold: an untitled booking
 * with both now COMPOSES them, and an untitled booking with an activity but NO KIND renders
 * BLANK where it used to render the activity on its own. The second half is deliberate and
 * follows from the table — with the kind mandatory on the form from 0.12.5 (Part I), a booking
 * with an activity and no kind is a legacy row, and there is no half-label for it.
 *
 * ⚠ PLAIN HYPHEN, NOT AN EN DASH. Ben wrote it as "Kind - Activity" and that is what ships. Per
 * his 2026-09-06 ruling a separator inside a LABEL is fine (the no-dash rule is about prose), so
 * this is a wording choice, not the dash rule — `bookingLabel` above keeps its en dash because
 * that is what Ben wrote THERE. Do not unify them without asking.
 *
 * ⚠ THE PERSON BRANCH IS REMOVED, NOT REORDERED. `bookingLabel` returns the PERSON when there is
 * no title, because it was written when the chip had ONE row. The chip now has three and row two
 * already carries the person, so on an untitled booking the name rendered TWICE — as
 * "Fred Smith – Navigation" under a row two reading "Smith, Fred" on a wide chip, and as a
 * straight duplicate on a narrow one. Row three must never render a person, under any width or
 * any combination of fields. Do not reinstate that branch here in any position.
 *
 * ⚠ KEEP THE BOOKING-KIND FALLBACK. The type stepping in where there is no description is Ben's
 * earlier reasoning and it still holds: a maintenance or unavailability chip has no person on row
 * two either, so dropping the kind would leave it showing a time and nothing else, with the type
 * surviving only as a bar colour. A colour is not a name to anyone who does not know the palette.
 *
 * ⚠ BLANK IS A LEGITIMATE RESULT. No title and no kind renders NOTHING — the activity does not
 * enter into it, per the table above. Do not substitute 'Booking' or anything else to fill the
 * row: `bookingLabel` does that because a one-line label had to say something; a third row does
 * not. Part I makes the kind mandatory on the FORM, not in the DATA, so kindless bookings still
 * exist on the board and this branch is live, not theoretical.
 */
export function taskRowLabel(
  b: Booking,
  kinds: Map<string, BookingKind>,
  activities: Map<string, TrainingActivity>,
): string {
  const title = b.title?.trim();
  if (title) return title;

  const kind = b.booking_kind_id ? kinds.get(b.booking_kind_id) : undefined;
  const kindName = kind?.name?.trim();
  // ⚠ NO KIND → BLANK, and it must return BEFORE the activity is even consulted. A kindless
  // booking has no left-hand side, so composing here would emit a dangling " - Activity"; and
  // returning the activity alone is the behaviour Ben replaced. Blank is the legitimate result —
  // do not substitute 'Booking' or the activity to fill the row.
  if (!kindName) return '';

  const act = activityName(b, activities)?.trim();
  // The separator lives only inside the branch that REQUIRES `act`, so a kind with no activity
  // returns the kind alone and the hyphen is never emitted. Same construction as bookingLabel's
  // dash above, and for the same reason: a dangling separator is impossible by structure, not by
  // luck. `act` is trimmed first so a whitespace-only activity name cannot produce one either.
  return act ? `${kindName} - ${act}` : kindName;
}

// Truncate a label to fit a chip of pixel width `w`.
//
// ⚠ `pxPerChar` DEFAULTS TO 5 so every pre-existing caller is untouched, but the chip now has
// three rows at three sizes and one flat budget is wrong for two of them: 5px/char is too
// generous for the 10px name row (it overflows) and too mean for the 7.5px task row (it
// truncates early for no reason). The per-row values live with the rows, in DayView.
export function truncateToWidth(label: string, w: number, leftPx: number, pxPerChar = 5): string {
  const maxChars = Math.max(0, Math.floor((w - leftPx) / pxPerChar));
  if (label.length <= maxChars) return label;
  return maxChars <= 1 ? '' : label.slice(0, maxChars - 1) + '…';
}
